import type { Reducer } from 'redux';

import type { MyGroupsLoadAction, MyGroupsLoadMoreAction } from '../../domains/myGroups/actions.types';
import type {
  AddFavoriteCommunityFulfilledAction,
  RemoveFavoriteCommunityFulfilledAction,
} from '../../domains/viewerCommunityRelationships/actions.types';

import type { FilteredMyGroups, MyGroups } from './types';
import { MyGroupsFilter } from './types';

export type MyGroupsAction =
  | MyGroupsLoadAction
  | MyGroupsLoadMoreAction
  | AddFavoriteCommunityFulfilledAction
  | RemoveFavoriteCommunityFulfilledAction;

const initialFilteredGroups: FilteredMyGroups = {
  groupIds: [],
  favoriteGroupIds: [],
  cursor: null,
  hasNextPage: false,
  status: 'pending',
};

const loadPendingMyGroups = (state: MyGroups, filteredBy: MyGroupsFilter) => ({
  ...state,
  [filteredBy]: initialFilteredGroups,
});

const loadRejectedMyGroups = (state: MyGroups, filteredBy: MyGroupsFilter) => ({
  ...state,
  [filteredBy]: {
    ...initialFilteredGroups,
    status: 'rejected',
  },
});

const loadMoreMyGroupsFulfilled = (state: MyGroups, filteredBy: MyGroupsFilter, data: FilteredMyGroups) => {
  const currentGroupIds = state[filteredBy]?.groupIds ?? [];
  const currentFavoriteGroupIds = state[filteredBy]?.favoriteGroupIds ?? [];
  const groupIds = [...currentGroupIds, ...data.groupIds];
  const favoriteGroupIds = currentFavoriteGroupIds.length > 0 ? currentFavoriteGroupIds : data.favoriteGroupIds;

  return {
    ...state,
    [filteredBy]: {
      ...data,
      groupIds,
      favoriteGroupIds,
    },
  };
};

const applyToAllFilters =
  (reducerWithFilter: (state: MyGroups, action: MyGroupsAction, filteredBy: MyGroupsFilter) => MyGroups) =>
  (state: MyGroups, action: MyGroupsAction) =>
    Object.values(MyGroupsFilter).reduce(
      (state, filteredBy: MyGroupsFilter) =>
        filteredBy in state ? reducerWithFilter(state, action, filteredBy) : state,
      state
    );

const addFavoriteFulfilled = applyToAllFilters(
  (state: MyGroups, action: AddFavoriteCommunityFulfilledAction, filteredBy: MyGroupsFilter) => {
    const filter = state[filteredBy];
    if (
      !filter ||
      !filter.groupIds.includes(action.payload.id) ||
      filter.favoriteGroupIds.includes(action.payload.id)
    ) {
      return state;
    }

    return {
      ...state,
      [filteredBy]: {
        ...state[filteredBy],
        favoriteGroupIds: [...filter.favoriteGroupIds, action.payload.id],
      },
    };
  }
);

const removeFavoriteFulfilled = applyToAllFilters(
  (state: MyGroups, action: RemoveFavoriteCommunityFulfilledAction, filteredBy: MyGroupsFilter) => {
    const filter = state[filteredBy];
    if (!filter || !filter.favoriteGroupIds.includes(action.payload.id)) {
      return state;
    }

    return {
      ...state,
      [filteredBy]: {
        ...state[filteredBy],
        favoriteGroupIds: filter.favoriteGroupIds.filter((id) => id !== action.payload.id),
      },
    };
  }
);

const initialState: MyGroups = {};

const reducer: Reducer<MyGroups, MyGroupsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'MY_GROUPS.LOAD_PENDING':
      return loadPendingMyGroups(state, action.payload.filteredBy);
    case 'MY_GROUPS.LOAD_FULFILLED':
      return { ...state, [action.payload.filteredBy]: action.payload.entities.myGroups };
    case 'MY_GROUPS.LOAD_REJECTED':
      return loadRejectedMyGroups(state, action.payload.filteredBy);
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return loadMoreMyGroupsFulfilled(state, action.payload.filteredBy, action.payload.entities.myGroups);
    case 'COMMUNITY.ADD_FAVORITE_FULFILLED':
      return addFavoriteFulfilled(state, action);
    case 'COMMUNITY.REMOVE_FAVORITE_FULFILLED':
      return removeFavoriteFulfilled(state, action);
    default:
      return state;
  }
};

export default reducer;
